import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { Card } from '../../atoms/Card/Card';
import { Stack } from '../../atoms/Stack/Stack';
import { Inline } from '../../atoms/Inline/Inline';
import { Button } from '../../atoms/Button/Button';
import { Input } from '../../atoms/Input/Input';
import { Icon } from '../../atoms/Icon';
import { Text } from '../../atoms/Typography/Text';
import { Section } from '../../templates/Section/Section';
import { AiLimitErrorMessage } from '../../aiUsage/AiLimitNotice';
import { invalidateAiUsage } from '../../../hooks/useAiUsage';
import { getAiLimitErrorKind } from '../../../services/aiUsageService';
import {
  extractMeasurementFromPhoto,
  type PhotoExtractionResult,
} from '../../../services/measurementService';
import { Alert } from '../../atoms/Alert/Alert';
import { LiveRegion } from '../../atoms/LiveRegion/LiveRegion';
import { convertHeicToJpeg, isHeicFile } from '../../atoms/PhotoUploadDropzone/heicConversion';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/heic',
]);
const COMPRESSED_MAX_DIMENSION = 1600;
const COMPRESSED_JPEG_QUALITY = 0.85;

export interface PhotoMeasurementCaptureProps {
  aquariumId: string;
  onExtracted: (result: PhotoExtractionResult) => void;
  onEnterManually: () => void;
  onCancel: () => void;
}

/**
 * Client-side canvas-based compression/resize. No new dependency is introduced —
 * this uses only the browser Canvas/Image APIs, per the epic's architecture decision.
 */
async function compressImageForUpload(file: File): Promise<Blob> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = objectUrl;
    });

    const { width, height } = image;
    const scale = Math.min(1, COMPRESSED_MAX_DIMENSION / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // No canvas support — fall back to the original file untouched.
      return file;
    }
    ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/jpeg', COMPRESSED_JPEG_QUALITY);
    });

    return blob ?? file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function validateSelectedFile(file: File, t: (key: string) => string): string | null {
  if (!ALLOWED_MIME_TYPES.has(file.type) && !isHeicFile(file)) {
    return t('measurement.capture.error.invalidType');
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return t('measurement.capture.error.tooLarge');
  }
  return null;
}

export function PhotoMeasurementCapture({
  aquariumId,
  onExtracted,
  onEnterManually,
  onCancel,
}: PhotoMeasurementCaptureProps) {
  const { t } = useTranslation();
  const [validationError, setValidationError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const errorRegionRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const mutation = useMutation<PhotoExtractionResult, Error, File>({
    mutationFn: async (file: File) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      // iPhone photos (HEIC) are converted to JPEG first: only Safari can draw them
      // on a canvas, and neither the backend nor the AI can read HEIC.
      const source = isHeicFile(file) ? await convertHeicToJpeg(file) : file;
      // Compression is an optimisation only — upload the original if the browser can't do it.
      const compressed = await compressImageForUpload(source).catch(() => source);
      return extractMeasurementFromPhoto(aquariumId, compressed, controller.signal);
    },
    onSuccess: (result) => {
      onExtracted(result);
    },
    // An extraction is an AI call: keep the credit meter current.
    onSettled: () => {
      invalidateAiUsage(queryClient);
    },
  });

  const isLoading = mutation.isPending;
  const isError = mutation.isError;

  useEffect(() => {
    if (isError) {
      errorRegionRef.current?.focus();
    }
  }, [isError]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      const error = validateSelectedFile(file, t);
      if (error) {
        setValidationError(error);
        return;
      }
      setValidationError(null);
      mutation.mutate(file);
    },
    [mutation, t],
  );

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort();
    mutation.reset();
    onCancel();
  }, [mutation, onCancel]);

  const handleRetry = useCallback(() => {
    mutation.reset();
    setValidationError(null);
    const input = fileInputRef.current;
    if (input) {
      input.value = '';
      input.focus();
      input.click();
    }
  }, [mutation]);

  const handleEnterManually = useCallback(() => {
    mutation.reset();
    onEnterManually();
  }, [mutation, onEnterManually]);

  return (
    <Section title={t('measurement.capture.sectionTitle')}>
      <Card className="photo-measurement-capture">
        <Stack gap="4">
          <Input
            ref={fileInputRef}
            id="photo-measurement-capture-file-input"
            type="file"
            accept="image/*"
            capture="environment"
            label={t('measurement.capture.fileInputLabel')}
            name="photo"
            disabled={isLoading}
            onChange={handleFileChange}
            error={validationError ?? undefined}
          />

          {isLoading && (
            <LiveRegion>
              <Inline gap="2" align="center">
                <Icon name="loader" ariaLabel={t('measurement.capture.loadingIconLabel')} />
                <Text color="muted">{t('measurement.capture.extracting')}</Text>
              </Inline>
            </LiveRegion>
          )}

          {isError &&
            (getAiLimitErrorKind(mutation.error) !== null ? (
              <AiLimitErrorMessage ref={errorRegionRef} tabIndex={-1} error={mutation.error} />
            ) : (
              <Alert ref={errorRegionRef} tabIndex={-1} title={t('measurement.capture.error.extractionFailed')}>
                {t('measurement.capture.error.hint')}
              </Alert>
            ))}

          <Inline gap="2" wrap>
            {isError ? (
              <>
                <Button variant="primary" onClick={handleRetry}>
                  {t('measurement.capture.retry')}
                </Button>
                <Button variant="secondary" onClick={handleEnterManually}>
                  {t('measurement.capture.enterManually')}
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={handleCancel}>
                {t('measurement.capture.cancel')}
              </Button>
            )}
          </Inline>
        </Stack>
      </Card>
    </Section>
  );
}
